import {
  formatHeaderAmount,
  isInvoiceApproved,
  isInvoicePaid,
} from "../../../modules/Invoice/pages/Wizard/pages/Details/headerSummary";

// Typing a prefix then a space scopes the palette to that module's search.
const searchModules = [
  { prefix: "w", module: "WAREHOUSE", label: "Warehouse" },
  { prefix: "a", module: "ARTIST", label: "Artists" },
  { prefix: "aw", module: "ARTWORK", label: "Artworks" },
  { prefix: "i", module: "INSTANCE", label: "Instances" },
  { prefix: "inv", module: "INVOICE", label: "Invoices" },
  { prefix: "c", module: "CONTACT", label: "Contacts" },
  { prefix: "cn", module: "CONSIGNMENT", label: "Consignments" },
  { prefix: "e", module: "EXHIBITION", label: "Exhibitions" },
  { prefix: "j", module: "JOB", label: "Jobs" },
  { prefix: "o", module: "OFFER", label: "Offers" },
  { prefix: "r", module: "RESERVATION", label: "Reservations" },
  { prefix: "s", module: "SHIPMENT", label: "Shipments" },
];

const resultTypes = {
  ARTIST: { label: "Artist", buildHref: ({ id }) => `/artist/${id}/details` },
  ARTWORK: {
    label: "Artwork",
    buildHref: ({ id }) => `/artworks/${id}/details`,
  },
  CONSIGNMENT: {
    label: "Consignment",
    buildHref: ({ id }) => `/consignment/${id}/form`,
  },
  CONTACT: {
    label: "Contact",
    buildHref: ({ id }) => `/contacts/${id}/details`,
  },
  EXHIBITION: {
    label: "Exhibition",
    buildHref: ({ id }) => `/exhibition/${id}/details`,
  },
  INSTANCE: {
    label: "Instance",
    buildHref: ({ id }) => `/instance/${id}/details`,
  },
  INVOICE: {
    label: "Invoice",
    buildHref: ({ id }) => `/invoice/${id}/details`,
  },
  JOB: { label: "Job", buildHref: ({ id }) => `/jobs/${id}/details` },
  // Offers have no page of their own; they are managed on the artwork.
  OFFER: {
    label: "Offer",
    buildHref: ({ offer }) =>
      offer?.artwork?.id ? `/artworks/${offer.artwork.id}/offers` : "/offer",
  },
  RESERVATION: {
    label: "Reservation",
    buildHref: ({ id }) => `/reservations/${id}/details`,
  },
  SHIPMENT: {
    label: "Shipment",
    buildHref: ({ id }) => `/shipments/${id}/details`,
  },
  WAREHOUSE_ITEM: {
    label: "Warehouse",
    buildHref: ({ id }) => `/warehouse/${id}/details`,
  },
};

const parseSearchModulePrefix = value => {
  const match = /^(\S+)\s+([\s\S]*)$/.exec(String(value ?? ""));
  const searchModule =
    match &&
    searchModules.find(
      candidate => candidate.prefix === match[1].toLowerCase(),
    );
  return searchModule ? { searchModule, rest: match[2] } : null;
};

const normalizeReferenceQuery = query =>
  String(query ?? "")
    .replace(/\s+/g, "")
    .toUpperCase();

// Every reference ends in a number, so a query without a digit is an action
// search rather than a reference lookup.
const isReferenceQuery = normalizedQuery =>
  normalizedQuery.length >= 2 && /\d/.test(normalizedQuery);

const normalizeModuleQuery = query =>
  String(query ?? "")
    .trim()
    .replace(/\s+/g, " ");

const isModuleQuery = moduleQuery => moduleQuery.replace(/ /g, "").length >= 2;

const labelStartsWith = (result, normalizedQuery) =>
  normalizeReferenceQuery(result?.label).startsWith(normalizedQuery);

// What to send for the current text, or null when nothing should be searched.
const buildSearchRequest = ({ query, searchModule }) => {
  if (searchModule) {
    const moduleQuery = normalizeModuleQuery(query);
    return isModuleQuery(moduleQuery)
      ? {
          kind: "module",
          key: `${searchModule.module}:${moduleQuery}`,
          group: searchModule.module,
          variables: { module: searchModule.module, query: moduleQuery },
        }
      : null;
  }
  const normalizedQuery = normalizeReferenceQuery(query);
  return isReferenceQuery(normalizedQuery)
    ? {
        kind: "reference",
        key: `REFERENCE:${normalizedQuery}`,
        group: "REFERENCE",
        variables: { query: normalizedQuery },
        normalizedQuery,
      }
    : null;
};

// Results from the previous request in the same group stay on screen until
// the new ones arrive; reference results are narrowed to the longer prefix.
const selectVisibleResults = (request, response) => {
  if (!request || response.group !== request.group) return [];
  return request.kind === "reference"
    ? response.results.filter(result =>
        labelStartsWith(result, request.normalizedQuery),
      )
    : response.results;
};

const formatArtistNames = artistNames =>
  String(artistNames ?? "")
    .split(/\n|,/)
    .map(name => name.trim())
    .filter(Boolean)
    .join(", ");

const describeInstanceArtwork = instance => {
  const artwork = instance?.edition?.artwork;
  return {
    title: artwork?.title,
    artistNames: formatArtistNames(instance?.artistNames),
    imageUrl: artwork?.imagesSummary?.imgT || "",
  };
};

const describeInstance = ({ instance }) => {
  const { title, artistNames, imageUrl } = describeInstanceArtwork(instance);
  return { details: [title, artistNames], imageUrl, showThumbnail: true };
};

const describeInvoice = ({ invoice }) => {
  const total = invoice?.invoiceTotals?.total;
  return {
    details: [
      [invoice?.contact?.name, invoice?.companyName].filter(Boolean).join(", "),
      total == null
        ? ""
        : `Total ${formatHeaderAmount(total, invoice?.currency?.code)}`,
    ],
    tags: [
      isInvoiceApproved(invoice) && "Approved",
      invoice?.isExported && "Exported",
      isInvoicePaid(invoice) && "Paid",
    ],
  };
};

const describeArtwork = ({ artwork }) => ({
  details: [(artwork?.artists || []).map(artist => artist?.name).join(", ")],
  imageUrl: artwork?.imagesSummary?.imgT || "",
  showThumbnail: true,
});

const describeContact = ({ contact }) => ({ details: [contact?.company] });

const joinNames = names => names.filter(Boolean).join(", ");

const firstLine = text =>
  String(text ?? "")
    .split(/\r?\n/)
    .find(line => line.trim())
    ?.trim();

const describeConsignment = ({ consignment }) => {
  const consignerName = consignment?.consigner?.name;
  const consigneeName = joinNames([
    consignment?.consigneeName,
    consignment?.consigneeCompany,
  ]);
  return {
    details: [
      consignerName && `From ${consignerName}`,
      consigneeName && `To ${consigneeName}`,
    ],
  };
};

const describeExhibition = ({ exhibition }) => ({
  details: [exhibition?.type, exhibition?.curator],
});

const describeJob = ({ job }) => ({
  details: [
    job?.jobType?.description,
    job?.jobStatus?.description,
    firstLine(job?.description),
  ],
});

const describeOffer = ({ offer }) => ({
  details: [
    offer?.artwork?.title,
    joinNames((offer?.artwork?.artists || []).map(artist => artist?.name)),
    offer?.contact?.name,
    offer?.status?.description,
  ],
  imageUrl: offer?.artwork?.imagesSummary?.imgT || "",
  showThumbnail: true,
});

const describeReservation = ({ reservation }) => ({
  details: [
    firstLine(reservation?.description),
    reservation?.reservationUser?.name,
  ],
});

const describeShipment = ({ shipment }) => ({
  details: [
    shipment?.shippingRef,
    shipment?.consignee?.name,
    shipment?.delCompany,
  ],
});

const describeWarehouseItem = ({ label, warehouseItem }) => {
  const instance = warehouseItem?.instance;
  const { title, artistNames, imageUrl } = describeInstanceArtwork(instance);
  return {
    details: [
      instance?.name,
      title,
      artistNames,
      warehouseItem?.itemDescription !== label &&
        warehouseItem?.itemDescription,
    ],
    imageUrl,
    showThumbnail: true,
  };
};

const describeResultByType = {
  ARTIST: () => ({}),
  ARTWORK: describeArtwork,
  CONSIGNMENT: describeConsignment,
  CONTACT: describeContact,
  EXHIBITION: describeExhibition,
  INSTANCE: describeInstance,
  INVOICE: describeInvoice,
  JOB: describeJob,
  OFFER: describeOffer,
  RESERVATION: describeReservation,
  SHIPMENT: describeShipment,
  WAREHOUSE_ITEM: describeWarehouseItem,
};

const buildSearchResults = (results = []) =>
  results
    .filter(result => resultTypes[result?.type])
    .map(result => {
      const {
        details = [],
        tags = [],
        imageUrl = "",
        showThumbnail = false,
      } = describeResultByType[result.type](result);
      return {
        key: `${result.type}-${result.id}`,
        label: result.label,
        typeLabel: resultTypes[result.type].label,
        href: resultTypes[result.type].buildHref(result),
        details: details.filter(Boolean),
        tags: tags.filter(Boolean),
        imageUrl,
        showThumbnail,
      };
    });

export {
  buildSearchRequest,
  buildSearchResults,
  normalizeReferenceQuery,
  parseSearchModulePrefix,
  searchModules,
  selectVisibleResults,
};
